package com.steamstreet.vegasful.web.concierge

import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime
import kotlinx.datetime.daysUntil

/*
 * How the concierge's answer labels dates: the ticket stub on each pick and the span in the
 * answer's heading. Shared so the labels can be tested on the JVM while the browser draws them.
 */

/** The three lines of a pick's ticket stub. A null line is left off. */
data class StubLabel(val days: String, val dates: String, val time: String?)

/**
 * The ticket stub for [dates] at [time]: "FRI / OCT 2 / 8 PM", or "FRI & SAT / OCT 2–3 / 8 PM" for
 * a merged pick. A single date that is [today] reads "TODAY". Null when there is no date at all,
 * which is true of a venue, a performer, or a thing to do.
 */
fun stubLabel(dates: List<LocalDate>, time: LocalTime?, today: LocalDate? = null): StubLabel? {
    if (dates.isEmpty()) return null
    val sorted = dates.distinct().sorted()
    val first = sorted.first()
    val last = sorted.last()
    val consecutive = first.daysUntil(last) == sorted.size - 1

    val days = when {
        sorted.size == 1 && first == today -> "TODAY"
        sorted.size == 1 -> first.dayOfWeek.short()
        sorted.size == 2 -> "${first.dayOfWeek.short()} & ${last.dayOfWeek.short()}"
        consecutive && sorted.size <= 7 -> "${first.dayOfWeek.short()}–${last.dayOfWeek.short()}"
        else -> "${sorted.size} DATES"
    }

    val span = when {
        sorted.size == 1 -> first.monthDay()
        first.month == last.month && consecutive -> "${first.monthDay()}–${last.day}"
        consecutive -> "${first.monthDay()}–${last.monthDay()}"
        sorted.size == 2 && first.month == last.month -> "${first.monthDay()}, ${last.day}"
        sorted.size == 2 -> "${first.monthDay()}, ${last.monthDay()}"
        else -> "FROM ${first.monthDay()}"
    }

    return StubLabel(days, span, time?.let { clockTime(it) })
}

/**
 * The span of [dates] an answer covers, for its heading: "Fri 2 Oct", or "Fri 2 – Sun 4 Oct", or
 * "Wed 30 Sep – Sun 4 Oct" across a month. Null when there are none.
 */
fun dateSpan(dates: List<LocalDate>): String? {
    val all = dates.sorted()
    val first = all.firstOrNull() ?: return null
    val last = all.last()
    fun LocalDate.long() = "${dayOfWeek.title()} $day"
    return when {
        first == last -> "${first.long()} ${first.month.title()}"
        first.month == last.month -> "${first.long()} – ${last.long()} ${last.month.title()}"
        else -> "${first.long()} ${first.month.title()} – ${last.long()} ${last.month.title()}"
    }
}

/** "8 PM", "8:30 PM", "12 AM". */
fun clockTime(time: LocalTime): String {
    val hour = when (val h = time.hour % 12) {
        0 -> 12
        else -> h
    }
    val suffix = if (time.hour < 12) "AM" else "PM"
    return if (time.minute == 0) "$hour $suffix" else "$hour:${time.minute.toString().padStart(2, '0')} $suffix"
}

private fun DayOfWeek.short() = name.take(3)

private fun DayOfWeek.title() = name.take(1) + name.substring(1, 3).lowercase()

private fun kotlinx.datetime.Month.title() = name.take(1) + name.substring(1, 3).lowercase()

private fun LocalDate.monthDay() = "${month.name.take(3)} $day"
